import { createAsyncThunk } from '@reduxjs/toolkit';
import {
  MotivationalSummaryPayload,
  UserFeedbackPayload,
  SyncedExternalPlatforms
} from 'interfaces';
import { ChatBotState, ChatRoles } from 'enums';
import focusBearApi, { isAbortLike } from 'services/axios-config';
import Endpoints from 'constants/endpoints';
import {
  updateError,
  updateIsAccountDeleted,
  updateIsAccountDeleting,
  updateIsLeaderBoardFetching,
  updateIsRequestDataConfirmationModalShown,
  updateIsRequestDataProcessing,
  updateShowPrivacyPolicyModal
} from '../setting/slice';
import { toast } from 'react-toastify';
import { RootState } from 'store';
import {
  toggleAreOnboardingStatsLoading,
  toggleAreUserDetailsLoading,
  updateIsCurrentActivityPropsUpdating,
  updateIsDailySummaryFetching,
  updateIsFetchingLogTermGoal,
  updateIsLogTermGoalUpdating,
  updateIsMotivationalSummaryLoading,
  updateIsMotivationalSummaryStopGenerating,
  updateIsSavingUserFeedback,
  updateIsUpdatingUserPrivacyPolicy,
  updateIsUpdatingUserProfileDescription,
  updateIsUpdatingUserProfileImage,
  updateIsUpdatingUserTimezone,
  updateIsUsernameUpdating,
  updateIsWeeklyFocusBlockSummaryUpdating,
  updateIsWeeklyStatsUpdating,
  updateMotivationalSummary,
  updateOpenAIPrompt,
  updateShowThankYouModal,
  updateUserMetadata,
  setUserDetailsTimezone,
  updateUsername,
  updateIsFetchingSyncedExternalPlatforms,
  updateIsSendingEmailVerification,
  updateIsFetchingUserDetailsSummary,
  updateIsFetchingUserFocusModes,
  updateIsCompletedFocusBlocksFetching
} from './slice';
import { AxiosError } from 'axios';
import { AXIOS_ERROR_CODE } from 'constants/axios-error-codes';
import { updateIsFocusTemplateInstalling } from '../focus-mode/slice';
import {
  EMPTY_STRING,
  HTTP_STATS_CODE,
  LOCAL_STORAGE,
  PAGINATE_ITEMS,
  QUESTION_MARK,
  OPEN_AI_RESPONSE_DATA,
  TIMEOUT_DELAY,
  FOCUS_LOG_PAGE_SIZE
} from 'constants/general';
import { getUserInstalledFocusTemplates } from '../focus-mode/extra';
import { updateInstallingPack } from '../habit-pack/slice';
import { getUserInstalledHabitPacks } from '../habit-pack/extra';
import { isValidIanaTimezone } from 'utils/time.util';
import {
  EventStreamContentType,
  fetchEventSource
} from '@microsoft/fetch-event-source';
import { getApiLanguageCode, runAfterDelay } from 'utils/helpers';
import {
  mapDailySummaryToWeeklyStats,
  mergeHomeWeekCompletionCounts
} from 'utils/home/home-week-chart.util';
import { BASE_URL } from 'constants/routes';
import {
  updateAiGeneratedContent,
  updateChatBotState,
  updateChatHistory,
  updateIsChatBotGenerating
} from '../chat-bot/slice';
import {
  updateIsAccessRequestGranted,
  updateIsAccessRequesting,
  updateShowRequestDetailsModal
} from '../admin/slice';
import {
  getActivitiesByUserIdOrStripeIdOrActivityType,
  getTasksByUserId,
  getUserInfoAndFocusModes
} from '../admin/extra';
import { getUserStatsLeaderBoard } from '../setting/extra';
import {
  FocusModeDaySummaryItem,
  UserTimezoneResponse,
  WeeklyFocusBlockSummary
} from 'interfaces/user.interface';
import { updateIsLongTermGoalsModalOpened } from '../modal/slice';
import { LEADER_BOARD } from 'constants/table';
import { AI_EVENT } from 'constants/events';
import { t } from 'services/i18n.service';
import { captureException } from 'services/posthog.service';
import { isRouteEmbedded } from 'utils/routes.util';
import { getLocalStorage } from 'utils/helpers/storage';
import {
  updateHasMotivationalDelayElapsed,
  setHasDataLoadFailed
} from '../general/slice';
import { Consent } from 'interfaces/argument.interace';
import {
  DEFAULT_SYNCED_EXTERNAL_PLATFORMS,
  DEFAULT_USER_CONSENTS
} from 'assets/data';
import { asArray } from 'utils/array.util';
import {
  isApiDataValid,
  isArray,
  isRecord
} from 'utils/api/api-data-validity.util';

export const getUserDetails = createAsyncThunk(
  'setting/get_user_details',
  async (_, { dispatch }) => {
    try {
      dispatch(toggleAreUserDetailsLoading(true));
      const { data } = await focusBearApi.get(Endpoints.USER_DETAILS);
      return data;
    } catch (error) {
      captureException(error);
    } finally {
      dispatch(toggleAreUserDetailsLoading(false));
    }
  }
);

export const getUserDetailsCurrentActivityProps = createAsyncThunk(
  'setting/get_user_details_current_activity_props',
  async (_, { dispatch, getState }) => {
    try {
      dispatch(updateIsCurrentActivityPropsUpdating(true));
      const {
        user: { currentActivityProps }
      } = getState() as RootState;
      const { data } = await focusBearApi.get(
        Endpoints.USER_DETAILS_CURRENT_ACTIVITY_PROPS
      );
      return data ?? currentActivityProps;
    } catch (error) {
      captureException(error);
    }
  }
);

export const getMorningActivityStats = createAsyncThunk(
  'setting/get_morning_activity_stats',
  async (sequence_id: string) => {
    try {
      const { data } = await focusBearApi.get(
        Endpoints.COMPLETED_ACTIVITY_SEQUENCE_STATS.replace(
          '{activity_sequence_id}',
          sequence_id
        )
      );
      return data ?? {};
    } catch (error) {
      captureException(error);
    }
  }
);

export const getEveningActivityStats = createAsyncThunk(
  'setting/get_evening_activity_stats',
  async (sequence_id: string) => {
    try {
      const { data } = await focusBearApi.get(
        Endpoints.COMPLETED_ACTIVITY_SEQUENCE_STATS.replace(
          '{activity_sequence_id}',
          sequence_id
        )
      );
      return data ?? {};
    } catch (error) {
      captureException(error);
    }
  }
);

export const updateUserSignedUpViaFocusTemplate = createAsyncThunk(
  'user/update_signed_up_via_focus_template',
  async (template_id: string, { dispatch }) => {
    try {
      const { status } = await focusBearApi.put(
        Endpoints.USER_SIGN_UP_TEMPLATE,
        {
          focus_mode_template_id: template_id
        }
      );
      if (status === HTTP_STATS_CODE.CREATED) {
        dispatch(getUserInstalledFocusTemplates());
        dispatch(getUserDetails());
      }
      dispatch(updateIsFocusTemplateInstalling(false));
    } catch (error) {
      captureException(error);
    }
  }
);

export const updateUserSignedUpViaHabitPack = createAsyncThunk(
  'user/update_signed_up_via_habit_pack',
  async (pack_id: string, { dispatch }) => {
    try {
      dispatch(updateInstallingPack(true));
      const { status } = await focusBearApi.put(
        Endpoints.USER_SIGN_UP_TEMPLATE,
        { pack_id }
      );
      if (status === HTTP_STATS_CODE.CREATED) {
        dispatch(getUserInstalledHabitPacks(true));
      }
      dispatch(updateInstallingPack(false));
    } catch (error) {
      captureException(error);
    }
  }
);

export const updateUserMetadataProfilePicture = createAsyncThunk(
  'user/update_user_metadata_profile_picture',
  async (
    profile_image: { url: string; file_path?: string },
    { dispatch, getState }
  ) => {
    try {
      dispatch(updateIsUpdatingUserProfileImage(true));
      const { details } = (getState() as RootState).user;
      const { status } = await focusBearApi.put(Endpoints.USER_METADATA, {
        profile_image
      });
      dispatch(updateIsUpdatingUserProfileImage(false));
      if (status === HTTP_STATS_CODE.SUCCESS) {
        dispatch(updateUserMetadata({ ...details?.metadata, profile_image }));
      }
    } catch (error) {
      dispatch(updateIsUpdatingUserProfileImage(false));
      captureException(error);
    }
  }
);

export const updateUserMetadataDescription = createAsyncThunk(
  'user/update_user_metadata_description',
  async (description: string, { dispatch, getState }) => {
    try {
      dispatch(updateIsUpdatingUserProfileDescription(true));
      const { details } = (getState() as RootState).user;
      const { status } = await focusBearApi.put(Endpoints.USER_METADATA, {
        description
      });
      dispatch(updateIsUpdatingUserProfileDescription(false));
      if (status === HTTP_STATS_CODE.SUCCESS) {
        dispatch(
          updateUserMetadata({
            ...details?.metadata,
            description: description
          })
        );
      }
    } catch (error) {
      dispatch(updateIsUpdatingUserProfileDescription(false));
      captureException(error);
    }
  }
);

export const getUserTimezone = createAsyncThunk(
  'user/get_user_timezone',
  async (_, { dispatch }) => {
    try {
      const { data } = await focusBearApi.get<UserTimezoneResponse>(
        Endpoints.USER_TIMEZONE
      );
      const timezone =
        typeof data?.timezone === 'string' && isValidIanaTimezone(data.timezone)
          ? data.timezone
          : EMPTY_STRING;
      dispatch(setUserDetailsTimezone(timezone));
      return timezone;
    } catch (error) {
      captureException(error);
    }
  }
);

export const updateUserTimezone = createAsyncThunk(
  'user/update_user_timezone',
  async (timezone: string, { dispatch }) => {
    try {
      dispatch(updateIsUpdatingUserTimezone(true));
      const { status, data } = await focusBearApi.patch<UserTimezoneResponse>(
        Endpoints.USER_TIMEZONE,
        { timezone }
      );
      if (status === HTTP_STATS_CODE.SUCCESS) {
        const nextTimezone =
          typeof data?.timezone === 'string' &&
          isValidIanaTimezone(data.timezone)
            ? data.timezone
            : timezone;
        dispatch(setUserDetailsTimezone(nextTimezone));
        toast.success(t('timezone_updated'));
      } else {
        toast.error(t('could_not_update_timezone'));
      }
    } catch (error) {
      toast.error(t('could_not_update_timezone'));
      captureException(error);
    } finally {
      dispatch(updateIsUpdatingUserTimezone(false));
    }
  }
);

export const fetchUserLevelStats = createAsyncThunk(
  'user/fetch_level_stats',
  async (_, { dispatch }) => {
    try {
      dispatch(toggleAreOnboardingStatsLoading(true));
      const { data } = await focusBearApi.get(Endpoints.USER_ONBOARDING_STATS);
      return data;
    } catch (error) {
      captureException(error);
    }
  }
);

let motivationalSummaryAbortController: AbortController | null = null;

export const fetchUserMotivationalSummary = createAsyncThunk(
  'user/fetch_user_motivational_summary',
  async (
    { tone, device_type, language }: MotivationalSummaryPayload,
    { dispatch, getState }
  ) => {
    if (motivationalSummaryAbortController) {
      motivationalSummaryAbortController.abort();
    }

    dispatch(updateHasMotivationalDelayElapsed(false));
    dispatch(updateIsMotivationalSummaryLoading(true));
    dispatch(updateMotivationalSummary(EMPTY_STRING));
    const controller = new AbortController();
    motivationalSummaryAbortController = controller;

    const isCurrentRequest = () =>
      motivationalSummaryAbortController === controller;

    const handleError = (error: unknown) => {
      if (!isCurrentRequest()) {
        return;
      }
      controller.abort();
      dispatch(updateIsMotivationalSummaryStopGenerating(true));
      dispatch(updateIsMotivationalSummaryLoading(false));
      dispatch(updateHasMotivationalDelayElapsed(true));
      captureException(error);
    };

    const timeoutId = runAfterDelay(() => {
      if (!isCurrentRequest() || controller.signal.aborted) {
        return;
      }
      const hasSummary = Boolean(
        (getState() as RootState).user.motivationalSummary
      );
      controller.abort();
      dispatch(updateIsMotivationalSummaryStopGenerating(true));
      dispatch(updateIsMotivationalSummaryLoading(false));
      if (!hasSummary) {
        dispatch(updateHasMotivationalDelayElapsed(true));
      }
    }, TIMEOUT_DELAY.MOTIVATIONAL_SUMMARY);

    try {
      await fetchEventSource(
        `${BASE_URL}${Endpoints.MOTIVATIONAL_SUMMARY}${QUESTION_MARK}${new URLSearchParams(
          {
            tone,
            device_type,
            language
          }
        ).toString()}`,
        {
          signal: controller.signal,
          headers: {
            Accept: 'text/event-stream',
            Authorization: `Bearer ${getLocalStorage(LOCAL_STORAGE.AUTH_BEARER_TOKEN)}`,
            Connection: 'keep-alive',
            'Cache-Control': 'no-cache'
          },
          async onopen(response) {
            if (
              response.ok &&
              response.headers.get('content-type') === EventStreamContentType
            ) {
              dispatch(updateIsMotivationalSummaryLoading(false));
              dispatch(updateIsMotivationalSummaryStopGenerating(false));
            } else {
              handleError('Failed to open stream');
            }
          },
          onmessage(event) {
            if (event.event === AI_EVENT.FATAL_ERROR) {
              handleError(AI_EVENT.FATAL_ERROR);
            } else {
              const { data } = event;
              if (
                data &&
                data !== OPEN_AI_RESPONSE_DATA.DONE &&
                !data.startsWith(OPEN_AI_RESPONSE_DATA.PROMPT)
              ) {
                dispatch(updateMotivationalSummary(data));
              } else if (data.startsWith(OPEN_AI_RESPONSE_DATA.PROMPT)) {
                dispatch(
                  updateOpenAIPrompt(
                    data
                      .replace(OPEN_AI_RESPONSE_DATA.PROMPT, EMPTY_STRING)
                      .trim()
                  )
                );
                dispatch(updateIsMotivationalSummaryStopGenerating(true));
              }
            }
          },
          onerror(error) {
            if (isAbortLike(error) || !isCurrentRequest()) {
              throw error;
            }
            handleError(error);
            throw error;
          }
        }
      );

      if (!isCurrentRequest()) {
        return;
      }

      const motivationalSummary =
        (getState() as RootState).user.motivationalSummary ?? '';
      if (!motivationalSummary) {
        handleError(
          'Response completed, but the event stream returned an empty response'
        );
      }
    } catch (error) {
      if (
        isAbortLike(error) ||
        controller.signal.aborted ||
        !isCurrentRequest()
      ) {
        return;
      }
      handleError(error);
    } finally {
      clearTimeout(timeoutId);
      if (motivationalSummaryAbortController === controller) {
        motivationalSummaryAbortController = null;
      }
    }
  }
);

export const sendMessage = createAsyncThunk(
  'user/send_message',
  async (abortController: AbortController, { dispatch, getState }) => {
    const {
      chatBot: { message, chatHistory }
    } = getState() as RootState;
    dispatch(updateAiGeneratedContent(EMPTY_STRING));
    dispatch(updateChatBotState(ChatBotState.WAITING));
    const currentMessage = { role: ChatRoles.USER, content: message };
    const body = JSON.stringify({
      chat: [...chatHistory, currentMessage],
      language: 'english'
    });
    dispatch(updateChatHistory(currentMessage));
    // This is similar to an EventSource, but allows
    // POST requests and headers
    const token = getLocalStorage(LOCAL_STORAGE.AUTH_BEARER_TOKEN);
    const headers = {
      Accept: 'text/event-stream',
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`
    };
    try {
      dispatch(updateIsChatBotGenerating(true));
      await fetchEventSource(`${BASE_URL}${Endpoints.AI_CHAT}`, {
        body,
        headers,
        method: 'POST',
        signal: abortController.signal,
        onmessage: ({ data }) => {
          if (data === OPEN_AI_RESPONSE_DATA.DONE) {
            dispatch(updateIsChatBotGenerating(false));
          } else if (data && data !== OPEN_AI_RESPONSE_DATA.DONE) {
            // This is a new word or chunk from the API stream response
            dispatch(updateChatBotState(ChatBotState.LOADING));
            dispatch(updateAiGeneratedContent(data));
          }
        }
      });
    } catch (error) {
      dispatch(updateChatBotState(ChatBotState.IDLE));
      dispatch(updateIsChatBotGenerating(false));
      dispatch(
        updateError({
          message: 'errors.couldnt_generate_chat_bot_message',
          status: HTTP_STATS_CODE.NOT_FOUND
        })
      );
      captureException(error);
    }
  }
);

export const updateUserConsent = createAsyncThunk(
  'user/update_user_consent',
  async (_, { dispatch }) => {
    try {
      dispatch(updateIsUpdatingUserPrivacyPolicy(true));

      const { status } = await focusBearApi.put(
        Endpoints.USER_CONSENT,
        DEFAULT_USER_CONSENTS
      );
      const isSuccess = status === HTTP_STATS_CODE.SUCCESS;
      isSuccess && dispatch(updateShowPrivacyPolicyModal(false));

      return isSuccess;
    } catch (error) {
      captureException(error);
    } finally {
      dispatch(updateIsUpdatingUserPrivacyPolicy(false));
    }
  }
);

export const updateUserConsents = createAsyncThunk(
  'user/update_user_consents',
  async (consents: Consent[]) => {
    try {
      await focusBearApi.put(Endpoints.USER_CONSENT, consents);
    } catch (error) {
      captureException(error);
    }
  }
);

export const updateUserAccessRequest = createAsyncThunk(
  'user/update_user_access_request',
  async (access_reason: string, { dispatch, getState }) => {
    try {
      const {
        admin: { accessRequestedUserId, skip }
      } = getState() as RootState;
      dispatch(updateIsAccessRequesting(true));
      const { status } = await focusBearApi.post(
        Endpoints.USER_ACCESS_REQUEST,
        {
          access_reason
        }
      );
      if (status === HTTP_STATS_CODE.CREATED) {
        dispatch(updateIsAccessRequestGranted(true));
        dispatch(updateIsAccessRequesting(false));
        dispatch(updateShowRequestDetailsModal(false));
        dispatch(getUserInfoAndFocusModes(accessRequestedUserId));
        dispatch(
          getActivitiesByUserIdOrStripeIdOrActivityType({
            user_id: accessRequestedUserId,
            page_num: (skip + PAGINATE_ITEMS) / PAGINATE_ITEMS
          })
        );
        dispatch(getTasksByUserId(accessRequestedUserId));
      } else {
        dispatch(updateShowPrivacyPolicyModal(false));
      }
    } catch (error) {
      dispatch(updateIsUpdatingUserPrivacyPolicy(false));
      toast.error(t('could_not_access_user_data'));
      captureException(error);
    }
  }
);

export const getCompletedActivityDaySummary = createAsyncThunk(
  'setting/get_completed_activity_day_summary',
  async (timezone: string, { dispatch }) => {
    try {
      dispatch(updateIsDailySummaryFetching(true));
      const { data } = await focusBearApi.get(
        Endpoints.COMPLETED_ACTIVITY_DAY_SUMMARY,
        {
          params: {
            timezone
          }
        }
      );
      return data ?? null;
    } catch (error) {
      dispatch(updateIsDailySummaryFetching(false));
      captureException(error);
    }
  }
);

export const updateLongTermGoals = createAsyncThunk(
  'user/update_long_term_goals',
  async (goals: string[], { dispatch }) => {
    try {
      dispatch(updateIsLogTermGoalUpdating(true));
      await focusBearApi.patch(Endpoints.USER_LONG_TERM_GOALS, {
        goals
      });
      dispatch(updateIsLongTermGoalsModalOpened(false));
      return goals;
    } catch (error) {
      !isRouteEmbedded && toast.error(t('could_not_update_long_term_goals'));
      dispatch(updateIsLogTermGoalUpdating(false));
      captureException(error);
    }
  }
);

// First page (or refresh) of the focus log — replaces the list.
export const getWeeklyFocusBlockSummary = createAsyncThunk(
  'user/get_weekly_focus_block_summary',
  async (
    params: { limit?: number; offset?: number } | undefined,
    { dispatch }
  ) => {
    try {
      dispatch(updateIsWeeklyFocusBlockSummaryUpdating(true));
      const { data } = await focusBearApi.get<WeeklyFocusBlockSummary[]>(
        Endpoints.USER_WEEKLY_FOCUS_BLOCK_SUMMARY,
        {
          params: {
            limit: params?.limit ?? FOCUS_LOG_PAGE_SIZE,
            offset: params?.offset ?? 0
          }
        }
      );
      return asArray<WeeklyFocusBlockSummary>(data);
    } catch (error) {
      dispatch(updateIsWeeklyFocusBlockSummaryUpdating(false));

      captureException(error);
    }
  }
);

// Subsequent pages — appended to the existing list (see slice reducer).
export const getMoreWeeklyFocusBlockSummary = createAsyncThunk(
  'user/get_more_weekly_focus_block_summary',
  async (params: { offset: number; limit?: number }) => {
    try {
      const { data } = await focusBearApi.get<WeeklyFocusBlockSummary[]>(
        Endpoints.USER_WEEKLY_FOCUS_BLOCK_SUMMARY,
        {
          params: {
            limit: params.limit ?? FOCUS_LOG_PAGE_SIZE,
            offset: params.offset
          }
        }
      );
      return asArray<WeeklyFocusBlockSummary>(data);
    } catch (error) {
      captureException(error);
      toast.error(t('could_not_fetch_data'));
    }
  }
);

export const getCompletedFocusBlocks = createAsyncThunk(
  'user/get_completed_focus_blocks',
  async (
    params: {
      focus_mode_id?: string;
      tag_id?: string;
      from_time?: string;
      to_time?: string;
    },
    { dispatch }
  ) => {
    try {
      dispatch(updateIsCompletedFocusBlocksFetching(true));
      const { data } = await focusBearApi.get<FocusModeDaySummaryItem[]>(
        Endpoints.COMPLETED_FOCUS_BLOCKS,
        {
          params: params ? { ...params } : {}
        }
      );
      return asArray<FocusModeDaySummaryItem>(data);
    } catch (error) {
      dispatch(updateIsCompletedFocusBlocksFetching(false));
      captureException(error);
    }
  }
);

export const updateLeaderBoardUsername = createAsyncThunk(
  'user/updateLeaderBoardUsername',
  async (username: string, { dispatch }) => {
    try {
      dispatch(updateIsUsernameUpdating(true));
      const { status } = await focusBearApi.put(Endpoints.USER_USERNAME, {
        username
      });
      dispatch(updateIsUsernameUpdating(false));
      if (status === HTTP_STATS_CODE.SUCCESS) {
        dispatch(updateUsername(username));
        dispatch(updateIsLeaderBoardFetching(true));
        dispatch(
          getUserStatsLeaderBoard({
            streak_type: EMPTY_STRING,
            limit: LEADER_BOARD.STAT_LIMIT
          })
        );
      } else {
        toast.error(t('could_not_update_username'));
      }
    } catch (error) {
      const err = error as AxiosError;
      if (err.response?.status === HTTP_STATS_CODE.BAD_REQUEST) {
        toast.error(t('could_not_update_offensive_username', { username }));
      } else {
        toast.error(t('could_not_update_username'));
      }
      dispatch(updateIsUsernameUpdating(false));
      captureException(error);
    }
  }
);

export const requestUserData = createAsyncThunk(
  'user/request_user_data',
  async (lang: string, { dispatch }) => {
    try {
      dispatch(updateIsRequestDataProcessing(true));
      const { status } = await focusBearApi.get(Endpoints.USER_DATA, {
        params: {
          language: getApiLanguageCode(lang)
        }
      });
      dispatch(updateIsRequestDataProcessing(false));
      if (status === HTTP_STATS_CODE.SUCCESS) {
        dispatch(updateIsRequestDataConfirmationModalShown(false));
        toast.success(t('user_data_success'));
      } else {
        toast.warning(t('could_not_process_your_data'));
      }
    } catch (error) {
      dispatch(updateIsRequestDataProcessing(false));
      toast.error(t('could_not_process_your_data'));
      captureException(error);
    }
  }
);

export const deleteUserAccount = createAsyncThunk(
  'user/delete_user_account',
  async (_, { dispatch }) => {
    try {
      dispatch(updateIsAccountDeleting(true));
      const { status } = await focusBearApi.delete(Endpoints.USER_DATA);
      dispatch(updateIsAccountDeleting(false));
      if (status === HTTP_STATS_CODE.SUCCESS) {
        dispatch(updateIsAccountDeleted(true));
      } else {
        toast.error(t('could_not_process_the_request'));
      }
    } catch (error) {
      dispatch(updateIsAccountDeleting(false));
      toast.error(t('could_not_process_the_request'));
      captureException(error);
    }
  }
);

export const getWeeklyStats = createAsyncThunk(
  'setting/get_weekly_status',
  async (_, { dispatch }) => {
    try {
      dispatch(updateIsWeeklyStatsUpdating(true));
      const [dailySummaryResponse, activitiesResponse, focusResponse] =
        await Promise.all([
          focusBearApi.get(Endpoints.USER_STATS_DAILY_SUMMARY),
          focusBearApi.get(Endpoints.WEEKLY_COMPLETED_ACTIVITY_SUMMARY),
          focusBearApi.get(Endpoints.USER_WEEKLY_FOCUS_BLOCK_SUMMARY, {
            params: { limit: FOCUS_LOG_PAGE_SIZE, offset: 0 }
          })
        ]);

      const days = mapDailySummaryToWeeklyStats(
        dailySummaryResponse.data ?? []
      );
      return mergeHomeWeekCompletionCounts({
        days,
        activities: asArray(activitiesResponse.data),
        focusBlocks: asArray(focusResponse.data)
      });
    } catch (error) {
      dispatch(updateIsWeeklyStatsUpdating(false));
      captureException(error);
    }
  }
);

export const saveUserFeedback = createAsyncThunk(
  'user/save_user_feedback',
  async (
    { rating, feedback, metadata, operating_system }: UserFeedbackPayload,
    { dispatch }
  ) => {
    try {
      dispatch(updateIsSavingUserFeedback(true));
      const { status } = await focusBearApi.post(
        Endpoints.USER_FEEDBACK,
        {
          rating,
          feedback,
          metadata
        },
        {
          headers: {
            operating_system
          }
        }
      );
      dispatch(updateIsSavingUserFeedback(false));
      if (status === HTTP_STATS_CODE.CREATED) {
        dispatch(updateShowThankYouModal(true));
      } else {
        toast.error(t('could_not_process_the_request'));
      }
    } catch (error) {
      toast.error(t('could_not_process_the_request'));
      dispatch(updateIsSavingUserFeedback(false));
      captureException(error);
    }
  }
);

export const getLongTermGoals = createAsyncThunk(
  'user/get_long_term_goals',
  async (_, { dispatch }) => {
    dispatch(updateIsFetchingLogTermGoal(true));
    try {
      const { data } = await focusBearApi.get<string[]>(
        Endpoints.USER_LONG_TERM_GOALS
      );
      if (isRouteEmbedded && !isApiDataValid(data, isArray)) {
        dispatch(setHasDataLoadFailed(true));
        return [];
      }
      return asArray<string>(data);
    } catch (error) {
      dispatch(updateIsFetchingLogTermGoal(false));
      captureException(error);
    }
  }
);

export const getSyncedExternalPlatforms = createAsyncThunk(
  'user/synced-external-platforms',
  async (_, { dispatch }) => {
    dispatch(updateIsFetchingSyncedExternalPlatforms(true));
    try {
      const { data } = await focusBearApi.get<SyncedExternalPlatforms>(
        Endpoints.USER_SYNCED_EXTERNAL_PLATFORMS
      );
      return data ?? DEFAULT_SYNCED_EXTERNAL_PLATFORMS;
    } catch (error) {
      dispatch(updateIsFetchingSyncedExternalPlatforms(false));
      captureException(error);
      return DEFAULT_SYNCED_EXTERNAL_PLATFORMS;
    }
  }
);

export const sendEmailVerification = createAsyncThunk(
  'user/send-email-verification',
  async (data: { email: string; lang: string }, { dispatch }) => {
    dispatch(updateIsSendingEmailVerification(true));
    try {
      const { status } = await focusBearApi.post(
        Endpoints.AUTH_SEND_EMAIL_VERIFICATION,
        {
          ...data,
          lang: getApiLanguageCode(data.lang)
        }
      );
      if (status === HTTP_STATS_CODE.CREATED) {
        toast.success(t('confirmation_email_sent_successfully'));
      }
      dispatch(updateIsSendingEmailVerification(false));
    } catch (error) {
      dispatch(updateIsSendingEmailVerification(false));
      captureException(error);
    }
  }
);

export const changePassword = createAsyncThunk(
  'user/change-password',
  async (data: { token: string; newPassword: string }) => {
    try {
      const { status } = await focusBearApi.post(
        Endpoints.AUTH_CHANGE_PASSWORD,
        data
      );
      return status;
    } catch (error) {
      captureException(error);
      return null;
    }
  }
);

export const getUserSummary = createAsyncThunk(
  'user/get_user_summary',
  async (_, { dispatch }) => {
    dispatch(updateIsFetchingUserDetailsSummary(true));
    try {
      const { data } = await focusBearApi.get(Endpoints.USER_DETAILS_SUMMARY);
      if (!isApiDataValid(data, isRecord)) {
        dispatch(setHasDataLoadFailed(true));
        return {};
      }
      return data;
    } catch (error) {
      const axiosError = error as AxiosError;
      if (
        axiosError.code === AXIOS_ERROR_CODE.CONNECTION_ABORTED ||
        axiosError.message?.includes('timeout')
      ) {
        captureException(error, {
          level: 'warning',
          extra: {
            message: 'User summary request timed out after 30 seconds',
            endpoint: Endpoints.USER_DETAILS_SUMMARY
          }
        });
      } else {
        captureException(error);
      }
      return {};
    } finally {
      dispatch(updateIsFetchingUserDetailsSummary(false));
    }
  }
);

export const getUserFocusModes = createAsyncThunk(
  'user/get_user_focus_modes',
  async (_, { dispatch }) => {
    dispatch(updateIsFetchingUserFocusModes(true));
    try {
      const { data } = await focusBearApi.get(Endpoints.USER_FOCUS_MODES);
      // Endpoint returns FocusMode[] directly; normalise defensively in case shape changes.
      return Array.isArray(data) ? data : (data?.focus_modes ?? []);
    } catch (error) {
      dispatch(updateIsFetchingUserFocusModes(false));
      captureException(error);
    }
  }
);

export const updateFocusBlockSummary = createAsyncThunk(
  'user/update_focus_block_summary',
  async (
    payload: {
      id: string;
      intention?: string;
      achievements?: string;
      distractions?: string;
      focus_duration_seconds?: number;
    },
    { dispatch }
  ) => {
    try {
      dispatch(updateIsWeeklyFocusBlockSummaryUpdating(true));
      await focusBearApi.patch(
        Endpoints.UPDATE_FOCUS_BLOCK_SUMMARY.replace(':id', payload.id),
        payload
      );
      dispatch(getWeeklyFocusBlockSummary());
    } catch (error) {
      toast.error(t('could_not_update_weekly_focus_summary'));
      captureException(error);
      throw error;
    } finally {
      dispatch(updateIsWeeklyFocusBlockSummaryUpdating(false));
    }
  }
);

export const deleteFocusBlockSummary = createAsyncThunk(
  'user/delete_focus_block_summary',
  async (id: string, { dispatch }) => {
    try {
      dispatch(updateIsWeeklyFocusBlockSummaryUpdating(true));
      await focusBearApi.delete(
        Endpoints.DELETE_FOCUS_BLOCK_SUMMARY.replace(':id', id)
      );
      dispatch(getWeeklyFocusBlockSummary());
    } catch (error) {
      toast.error(t('could_not_update_weekly_focus_summary'));
      captureException(error);
      throw error;
    } finally {
      dispatch(updateIsWeeklyFocusBlockSummaryUpdating(false));
    }
  }
);

export type CreateManualFocusBlockPayload = {
  intention?: string;
  achievements?: string;
  distractions?: string;
  focus_duration_seconds: number;
  start_time: string;
  finish_time: string;
  focus_mode_id?: string;
};

export const createManualFocusBlockSummary = createAsyncThunk(
  'user/create_manual_focus_block_summary',
  async (payload: CreateManualFocusBlockPayload, { dispatch }) => {
    try {
      dispatch(updateIsWeeklyFocusBlockSummaryUpdating(true));

      const { focus_mode_id, ...body } = payload;
      await focusBearApi.post(Endpoints.CREATE_FOCUS_BLOCK_SUMMARY, {
        ...body,
        ...(focus_mode_id ? { focus_mode_id } : {})
      });
      dispatch(getWeeklyFocusBlockSummary());
    } catch (error) {
      toast.error(t('could_not_update_weekly_focus_summary'));
      captureException(error);
      throw error;
    } finally {
      dispatch(updateIsWeeklyFocusBlockSummaryUpdating(false));
    }
  }
);
